import React, { useState } from 'react'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { Image as ImageIcon, Video, Zap, ArrowRight, ArrowLeft } from 'lucide-react'
import styled from 'styled-components'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import ImageInferenceTool from './ImageInferenceTool'
import VideoInferenceTool from './VideoInferenceTool'

type InferenceType = 'image' | 'video' | null

const ViewContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: transparent;
  padding: 1.5rem;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
`

const BackButtonContainer = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1000;
`

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  flex-shrink: 0;

  h1 {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
    background: linear-gradient(90deg, #fff 0%, rgba(102, 126, 234, 1) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
  }
`

const ContentContainer = styled(Container)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 1000px;
  min-height: 0;
  overflow: hidden;
  padding: 0 !important; /* Override bootstrap padding if necessary */
  padding-top: 1rem !important;
`

const InferenceTypeCard = styled.div<{ $disabled?: boolean }>`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 2rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  height: 100%; /* Ensure cards take full height of column */
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      `
      border-color: rgba(102, 126, 234, 0.4);
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
      transform: translateY(-4px);
    `}
  }
`

const CardContent = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  min-width: 80px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
`

const CardInfo = styled.div`
  flex: 1;
  text-align: left;
`

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.5rem;
`

const CardDescription = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.5;
`

const CardAction = styled.div`
  display: flex;
  align-items: center;
  color: rgba(102, 126, 234, 1);
  font-weight: 500;
  gap: 0.5rem;
`

const ModelStats = styled.div`
  margin-top: 0;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  text-align: center;
  flex-shrink: 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
`

const StatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
`

const StatItem = styled.div`
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: rgba(102, 126, 234, 1);
    margin-bottom: 0.25rem;
  }

  .stat-label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`

const InferencingToolView: React.FC = () => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const [selectedInferenceType, setSelectedInferenceType] = useState<InferenceType>(null)

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  // Calculate statistics
  const stats = {
    totalModels: modelsData?.models?.length || 0,
    readyForInference:
      modelsData?.models?.filter((m) => m.state.toLowerCase() === 'trained').length || 0,
  }

  const handleImageInference = () => {
    setSelectedInferenceType('image')
  }

  const handleVideoInference = () => {
    setSelectedInferenceType('video')
  }

  const handleBack = () => {
    setSelectedInferenceType(null)
  }

  const isDisabled = stats.readyForInference === 0

  // Show specific inference tool if selected
  if (selectedInferenceType === 'image') {
    return <ImageInferenceTool onBack={handleBack} />
  }

  if (selectedInferenceType === 'video') {
    return <VideoInferenceTool onBack={handleBack} />
  }

  // Show selection screen

  return (
    <ViewContainer>
      {/* Page Header */}
      <PageHeader>
        <h1>Inferencing</h1>
        <p>Choose your inference type to get started with predictions</p>
      </PageHeader>

      <ContentContainer fluid>
        {/* Model Statistics */}
        <ModelStats className="mb-4" style={{ marginTop: 0 }}>
          <StatsRow>
            <StatItem>
              <div className="stat-value">{stats.totalModels}</div>
              <div className="stat-label">Total Models</div>
            </StatItem>
            <StatItem>
              <div className="stat-value">{stats.readyForInference}</div>
              <div className="stat-label">Ready for Inference</div>
            </StatItem>
          </StatsRow>
        </ModelStats>

        {/* Inference Type Options */}
        <Row className="g-4">
          <Col xs={12} md={6}>
            <InferenceTypeCard onClick={handleImageInference} $disabled={isDisabled}>
              <CardContent>
                <IconWrapper>
                  <ImageIcon size={40} />
                </IconWrapper>
                <CardInfo>
                  <CardTitle>Image Inference</CardTitle>
                  <CardDescription>
                    Upload images or select from datasets to run predictions with your trained
                    models
                  </CardDescription>
                </CardInfo>
                {!isDisabled && (
                  <CardAction>
                    <span>Start</span>
                    <ArrowRight size={20} />
                  </CardAction>
                )}
              </CardContent>
            </InferenceTypeCard>
          </Col>
          <Col xs={12} md={6}>
            <InferenceTypeCard onClick={handleVideoInference} $disabled={isDisabled}>
              <CardContent>
                <IconWrapper>
                  <Video size={40} />
                </IconWrapper>
                <CardInfo>
                  <CardTitle>Video Inference</CardTitle>
                  <CardDescription>
                    Process video files frame-by-frame to detect objects and generate predictions
                  </CardDescription>
                </CardInfo>
                {!isDisabled && (
                  <CardAction>
                    <span>Start</span>
                    <ArrowRight size={20} />
                  </CardAction>
                )}
              </CardContent>
            </InferenceTypeCard>
          </Col>
        </Row>
      </ContentContainer>
    </ViewContainer>
  )
}

export default InferencingToolView
